import { Module } from 'vuex';

import { getEnv } from '@/constants';
import { mattermostApiClient } from '@/services/apiClient';
import { mmEventSourceClient } from '@/services/eventSourceClient/mmEventSourceClient';
import loggerService from '@/services/logger.service';
import trackerService from '@/services/tracker.service';
import store, { RootState } from '@/store';

let eventSource: mmEventSourceClient | null = null;

export type EventSourceState = {
  timeout: number | null;
  persist: boolean;
  connectionRetries: number;
  firstConnectDone: boolean;
  eventSourceStatus: 'not-initiated' | 'connecting' | 'connected' | 'error' | 'closed' | 'bailed';
  connectionId: string | null;
};

const eventsourcemodule: Module<EventSourceState, RootState> = {
  namespaced: true,
  state: {
    timeout: null,
    persist: false,
    connectionRetries: 0,
    firstConnectDone: false,
    eventSourceStatus: 'not-initiated',
    connectionId: ''
  },

  mutations: {
    setEventSourceStatus(state, status) {
      state.eventSourceStatus = status;
    },
    setConnectionId(state, connectionId) {
      state.connectionId = connectionId;
    }
  },
  actions: {
    setConnectionId(context, msg) {
      try {
        const connectionId = msg?.data?.connection_id;
        context.commit('setConnectionId', connectionId);
      } catch (e) {
        loggerService.error('Failed to parse socket message: ', msg);
        msg = 'Unparsable string: ' + msg;
      }
    },
    updateRoomForSSE(context, roomId: string) {
      if (!context.rootState.eventsource.connectionId) {
        loggerService.warn('SSE-mmsocket: no connection id found to update the room');
        return;
      }
      const data = {
        room_id: roomId,
        connection_id: context.rootState.eventsource.connectionId
      };

      context.dispatch(
        'mattermost/makeMattermostCall',
        {
          url: `/api/v4/sse/updateRoom`,
          method: 'POST',
          body: data
        },
        { root: true }
      );
    },
    close(context) {
      if (!eventSource) {
        loggerService.warn('SSE-mmsocket: no event source found to close the connection');
        return;
      }
      eventSource.close();
    },
    async connect(context) {
      if (context.state.firstConnectDone || eventSource?.openPromise) {
        return;
      }
      if (!eventSource) {
        loggerService.info('SSE: creating SSE instance..');
        // we need token to create event source instance, so need to check if mm auth token
        // is available or not, if not we have to fetch one.
        await preConnect()
          .then(() => {
            createEventSourceInstance();
          })
          .catch(err => {
            console.error('Error in preconnect for sse', err);
            // implement retries here
          });
      }
      if (eventSource) {
        trackerService.track('serverSentEvent', 'connectionInitialized', { timestamp: Date.now() });
        eventSource.connect();
      } else {
        loggerService.warn(
          'SSE: event source is null, could not connect to sse server..',
          context.rootState.route.params.id
        );
      }
    }
  },
  getters: {
    shouldUseSSE(state, getters, rootState) {
      return !rootState.mattermost.mmEverConnected;
    }
  }
};
export default eventsourcemodule;

function preConnect() {
  if (!mattermostApiClient.authToken) {
    return mattermostApiClient.onUnauthorized() as Promise<void>;
  }
  return Promise.resolve();
}

function createEventSourceInstance() {
  const mmTeamId = store.state.event.event?.mmid;
  const token = mattermostApiClient.authToken;
  const sourceUrl = `${getEnv('VUE_APP_API_MATTERMOST')}/api/v4/sse/?token=${token}&team_id=${mmTeamId}`;
  eventSource = new mmEventSourceClient(sourceUrl, loggerService);
}
